/**
 * Settings Page
 *
 * User account settings including:
 * - Profile information
 * - Security (MFA, password)
 * - Preferences
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Settings, User, Shield, Bell, Link } from 'lucide-react';
import { MFASettings } from '../components/settings';
import { PlatformConnections } from '../components/PlatformConnections';

type SettingsTab = 'profile' | 'security' | 'notifications' | 'platforms';

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('security');

  const tabs = [
    { id: 'profile' as const, label: 'Profile', icon: User },
    { id: 'security' as const, label: 'Security', icon: Shield },
    { id: 'notifications' as const, label: 'Notifications', icon: Bell },
    { id: 'platforms' as const, label: 'Platforms', icon: Link },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Settings className="w-8 h-8 text-purple-600" />
            <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          </div>
          <p className="text-gray-600">Manage your account settings and preferences</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition relative ${
                    activeTab === tab.id
                      ? 'text-purple-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600"
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'profile' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Profile Settings</h2>
              <p className="text-gray-600">Profile settings coming soon...</p>
            </div>
          )}

          {activeTab === 'security' && <MFASettings />}

          {activeTab === 'notifications' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Notification Preferences</h2>
              <p className="text-gray-600">Notification settings coming soon...</p>
            </div>
          )}

          {activeTab === 'platforms' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <PlatformConnections />
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
